/**
 * Wires the "Suggested" operands (src/lib/operandSuggestions.ts) into the form
 * builder's formula pickers (z8nrz7deww). The page provides what the pickers
 * cannot see themselves: every saved form (the formula corpus) and the form
 * being edited, live, so its unsaved formulas count too.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formsApi } from '@/api/formsApi'
import { allFields } from '@/lib/formSections'
import { crossFormKey, parseCrossFormRef } from '@/lib/crossForm'
import { extractFormulaRefs } from '@/lib/formula'
import { buildCorpus, suggestOperands, type CorpusForm, type FormulaCorpus, type Suggestion } from '@/lib/operandSuggestions'
import type { RefFormGroup } from './FormFieldRefSelect'

interface Ctx {
  corpus: FormulaCorpus
  /** The edited form's key prefix: its handle, or a stand-in while it has none. */
  handle: string
}

const SuggestionContext = createContext<Ctx | null>(null)

/** A handle no saved form can have (handles are lowercase identifiers). */
const UNSAVED = '~this-form'

export function OperandSuggestionProvider({
  handle,
  fields,
  children,
}: {
  /** The edited form's handle; absent before its first save. */
  handle?: string | null
  /** The edited form's fields as they are now, unsaved edits included. */
  fields: { id: string; libraryFieldId?: string; formula?: string }[]
  children: ReactNode
}) {
  // Same query (and cache) as the cross-form picker's: every form definition.
  const defs = useQuery({
    queryKey: ['form-definitions', 'list-for-cross-refs'],
    queryFn: () => formsApi.listDefinitions(),
    staleTime: 5 * 60_000,
  })
  const own = handle || UNSAVED
  const value = useMemo<Ctx>(() => {
    // The newest version of each form; the edited one as it is on screen.
    const forms: CorpusForm[] = []
    const seen = new Set<string>([own])
    for (const def of [...(defs.data?.items ?? [])].sort((a, b) => (b.version ?? 0) - (a.version ?? 0))) {
      if (!def.handle || seen.has(def.handle)) continue
      seen.add(def.handle)
      forms.push({ handle: def.handle, fields: allFields(def) })
    }
    forms.push({ handle: own, fields })
    return { corpus: buildCorpus(forms), handle: own }
  }, [defs.data, fields, own])
  return <SuggestionContext.Provider value={value}>{children}</SuggestionContext.Provider>
}

/**
 * The suggestions for a picker offering `groups`, given the formula so far.
 * `owner` is the field whose formula this is (its saved copy is left out).
 * Empty outside a provider, with nothing placed, or with nothing paired.
 */
export function useOperandSuggestions(groups: RefFormGroup[], formula: string, owner?: string): Suggestion[] {
  const ctx = useContext(SuggestionContext)
  return useMemo(() => {
    if (!ctx) return []
    const keyOf = (ref: string) => (parseCrossFormRef(ref) ? ref : crossFormKey(ctx.handle, ref))
    const identity = (ref: string) => {
      const key = keyOf(ref)
      return ctx.corpus.identityOf.get(key) ?? key
    }
    const candidates = groups.flatMap(g =>
      g.fields.map(f => ({ refKey: f.refKey, label: f.label, identity: identity(f.refKey), onThisForm: g.key === '' })),
    )
    const labels = new Map(candidates.map(c => [c.identity, c.label]))
    return suggestOperands({
      candidates,
      placed: extractFormulaRefs(formula).map(identity),
      corpus: ctx.corpus,
      exclude: owner ? crossFormKey(ctx.handle, owner) : undefined,
      labelOf: id => labels.get(id) ?? 'a field in this formula',
    })
  }, [ctx, groups, formula, owner])
}
